import { forwardRef } from 'react';
import {
  Listbox as HeadlessListbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { Check } from 'lucide-react';
import type { ControllerRenderProps } from 'react-hook-form';

import { cn } from '#/lib/utils';
import { ChevronDown } from '#/components/ChevronDown';

export type ListboxOptions = Array<{
  label: string;
  value: string;
}>;

type SharedProps = Partial<Omit<ControllerRenderProps, 'value' | 'onChange'>> &
  Omit<
    React.InputHTMLAttributes<HTMLSelectElement>,
    'value' | 'onChange' | 'multiple' | 'defaultValue'
  > & {
    className?: string;
    displayValue?: string;
    options: ListboxOptions;
    theme?: 'light' | 'dark';
  };

type SingleListboxProps = SharedProps & {
  multiple?: false;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
};

type MultipleListboxProps = SharedProps & {
  multiple: true;
  value?: string[];
  defaultValue?: string | string[];
  onChange?: (value: string[]) => void;
};

export type ListboxProps = SingleListboxProps | MultipleListboxProps;

function hasSelection(value: string | string[] | undefined, multiple: boolean) {
  if (multiple) {
    return Array.isArray(value) && value.length > 0;
  }
  return typeof value === 'string' && value !== '';
}

function selectedLabel(
  value: string | string[] | undefined,
  options: ListboxOptions,
  displayValue: string | undefined,
  multiple: boolean,
) {
  if (displayValue) {
    return displayValue;
  }
  if (multiple) {
    const selected = Array.isArray(value) ? value : [];
    return selected
      .map(
        (item) =>
          options.find((option) => option.value === item)?.label ?? item,
      )
      .join(', ');
  }
  if (typeof value !== 'string') {
    return '';
  }
  return options.find((option) => option.value === value)?.label ?? value;
}

export const Listbox = forwardRef<HTMLSelectElement, ListboxProps>(
  (props, ref) => {
    const {
      className,
      options,
      placeholder,
      value,
      displayValue,
      onChange,
      name,
      defaultValue,
      theme,
      multiple = false,
    } = props;
    const selected = hasSelection(value, multiple);

    return (
      <div className={cn('relative w-full', className)}>
        <HeadlessListbox
          defaultValue={defaultValue}
          value={multiple ? (Array.isArray(value) ? value : []) : value}
          onChange={onChange as (value: string | string[]) => void}
          name={name}
          ref={ref}
          as="div"
          multiple={multiple}
          className="group w-full"
        >
          <ListboxButton
            className={cn('w-full trigger', {
              'text-grayscale-04': !selected,
              'trigger-dark': theme === 'dark',
            })}
          >
            <span className="min-w-0 truncate pr-20 text-left">
              {selected
                ? selectedLabel(value, options, displayValue, multiple)
                : placeholder}
            </span>
            <ChevronDown className="icon-indicator" />
          </ListboxButton>
          <ListboxOptions
            anchor={{
              to: 'bottom',
              gap: 6,
            }}
            transition
            className={cn('w-[var(--button-width)] options-container', {
              'options-container-dark': theme === 'dark',
            })}
          >
            {options.map((option) => (
              <ListboxOption
                className={cn('group options-item whitespace-normal', {
                  'options-item-dark': theme === 'dark',
                })}
                key={option.value}
                value={option.value}
              >
                <Check className="options-item-checked" />
                {option.label}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </HeadlessListbox>
      </div>
    );
  },
);

Listbox.displayName = 'Listbox';
